import { enqueueSnackbar } from 'notistack';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';

import { MLInput } from 'components/Input/Input';
import { useCreateEmployeeForClient } from 'hooks/Clients/smartUsersForClientCreate/useCreateEmployeeForClient';

interface CreateEmployeeModalProps {
  isOpen: boolean;
  onClose: () => void;
  isAddingEmployee: boolean;
}

const CreateEmployeeModal = ({ isOpen, onClose, isAddingEmployee }: CreateEmployeeModalProps) => {
  const { createEmployee, isLoading: isLoadingEmployee } = useCreateEmployeeForClient();
  const { t } = useTranslation();

  const {
    handleSubmit: handleEmployeeSubmit,
    control: employeeControl,
    reset: resetEmployee,
    setError: setEmployeeError,
  } = useForm({
    defaultValues: {
      first_name: '',
      last_name: '',
      email: '',
      phone_number: '',
      password: '',
      confirm_password: '',
      send_email_notifications: false,
    },
  });

  const onCloseModal = () => {
    onClose();
    resetEmployee();
  };

  const onCreateEmployee = (employeeData: any) => {
    createEmployee(employeeData, {
      onSuccess: () => {
        enqueueSnackbar(t('Modals.manage-manager.successCreate'), { variant: 'success' });
        onCloseModal();
      },
      onError: (error: any) => {
        handleEmployeeError(error);
        enqueueSnackbar(t('Modals.manage-manager.failCreate'), { variant: 'error' });
      },
    });
  };

  const handleEmployeeError = (error: any) => {
    if (error.response?.data) {
      const errorData = error.response.data;

      // Handle field-specific errors
      if (errorData.first_name) {
        setEmployeeError('first_name', { message: errorData.first_name[0] });
      }
      if (errorData.last_name) {
        setEmployeeError('last_name', { message: errorData.last_name[0] });
      }
      if (errorData.email) {
        setEmployeeError('email', { message: errorData.email[0] });
      }
      if (errorData.phone_number) {
        setEmployeeError('phone_number', { message: errorData.phone_number[0] });
      }
      if (errorData.password) {
        setEmployeeError('password', { message: errorData.password[0] });
      }
      if (errorData.confirm_password) {
        setEmployeeError('confirm_password', { message: errorData.confirm_password[0] });
      }

      // Handle non-field errors (like password mismatch)
      if (errorData.non_field_errors) {
        setEmployeeError('confirm_password', { message: errorData.non_field_errors[0] });
        setEmployeeError('password', { message: errorData.non_field_errors[0] });
      }
    } else {
      enqueueSnackbar(t('Modals.manage-manager.failCreate'), { variant: 'error' });
    }
  };

  return (
    <div>
      <Modal
        isOpen={isOpen}
        toggle={() => {
          // onReset();
          onClose();
        }}
        size="lg"
        backdrop="static"
        keyboard={false}
      >
        <ModalHeader
          className="border-bottom py-3"
          toggle={() => {
            // onReset();
            onClose();
          }}
        >
          <div className="d-flex justify-content-between align-items-center w-100 gap-5">
            <div className="text-purple">
              {isAddingEmployee
                ? t('Modals.manage-manager.header-site-add')
                : t('Modals.manage-manager.header-site-edit')}
            </div>
            <Button className="btn btn-ghost-purple btn-sm" onClick={() => onCloseModal()}>
              {isAddingEmployee
                ? t('Modals.manage-client.backToSiteCreation')
                : t('Modals.manage-client.backToSiteEdit')}
            </Button>
          </div>
        </ModalHeader>
        <ModalBody>
          <div className="row">
            <div className="col-12">
              <div className="row mb-3">
                <h5 className="mb-1">{t('Modals.manage-client.createNewManager')}</h5>
              </div>
              <div className="row mb-3">
                <div className="col-md-6">
                  <Controller
                    control={employeeControl}
                    name="first_name"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-manager.firstName')}
                        error={error?.message}
                        required
                      />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={employeeControl}
                    name="last_name"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput {...field} label={t('Modals.manage-manager.lastName')} error={error?.message} required />
                    )}
                  />
                </div>
              </div>

              <div className="row mb-3">
                <div className="col-md-6">
                  <Controller
                    control={employeeControl}
                    name="email"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-manager.email')}
                        type="email"
                        error={error?.message}
                        required
                      />
                    )}
                  />
                  <Controller
                    control={employeeControl}
                    name="send_email_notifications"
                    render={({ field }) => (
                      <>
                        <div className="form-check mt-1">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            id="smstReceiveMessagesCheckbox"
                            checked={field.value}
                            onChange={e => field.onChange(e.target.checked)}
                          />
                          <Label className="form-check-label mb-0 text-muted" htmlFor="smstReceiveMessagesCheckbox">
                            Send account credentials via email
                          </Label>
                        </div>
                      </>
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={employeeControl}
                    name="phone_number"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput {...field} label={t('Modals.manage-manager.phone')} type="tel" error={error?.message} />
                    )}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-6">
                  <Controller
                    control={employeeControl}
                    name="password"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-manager.password')}
                        type="password"
                        error={error?.message}
                        required
                      />
                    )}
                  />
                </div>
                <div className="col-md-6">
                  <Controller
                    control={employeeControl}
                    name="confirm_password"
                    render={({ field, fieldState: { error } }) => (
                      <MLInput
                        {...field}
                        label={t('Modals.manage-manager.confirmPassword')}
                        type="password"
                        error={error?.message}
                        required
                      />
                    )}
                  />
                </div>
              </div>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button className="btn btn-ghost-danger" onClick={onCloseModal}>
            {t('Modals.delete-modal.cancel')}
          </Button>
          <Button
            className="btn btn-ghost-success"
            onClick={handleEmployeeSubmit(onCreateEmployee)}
            disabled={isLoadingEmployee}
          >
            {isLoadingEmployee ? t('Modals.manage-client.creating') : t('Modals.manage-manager.createManager')}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default CreateEmployeeModal;
